import axios, { AxiosError } from "axios";

const WEB_API_URL = process.env.REACT_APP_WEB_API_URL;
const PLATFORM = "web";

// Only attach an Authorization header when we have a genuine token. A missing /
// empty / "null" / "undefined" token must go out as a logged-out (anonymous)
// view with NO header — never as `Bearer null`, which the API records as
// auth_failed instead of an anonymous view.
function bearerHeader(accessToken: string | null | undefined): Record<string, string> {
    if (!accessToken || accessToken === "null" || accessToken === "undefined") return {};
    return { Authorization: "Bearer " + accessToken };
}

/**
 * Live view beacon (page still alive). `watchSeconds` must be the CUMULATIVE
 * seconds watched this session and must grow over the session (5, 25, 305, …) —
 * the server keeps the max per (video, viewer) in a rolling 4h window, so
 * per-interval deltas would record only one interval's worth. Fire-and-forget:
 * never block playback on the result.
 */
export async function PostVideoView(
    videoId: number,
    watchSeconds: number,
    accessToken: string | null,
    abortController: AbortController
): Promise<void> {
    try {
        await axios.post(
            `${WEB_API_URL}v1/videos/${videoId}/view`,
            { watch_seconds: watchSeconds, platform: PLATFORM },
            {
                signal: abortController.signal,
                headers: bearerHeader(accessToken),
            }
        );
    } catch (error) {
        const err = error as AxiosError;
        if ((err as any)?.name === "CanceledError") return;
        // 422 = insufficient_watch_time (watch_seconds < 5). Expected for very
        // early beacons — ignore. Anything else: log once, never retry blindly.
        if (err.response?.status === 422) return;
        console.log("PostVideoView error:", err);
    }
}

/**
 * Unload / tab-hidden flush of the same cumulative view beacon. Must survive the
 * page going away, so it uses a keepalive transport rather than a normal request.
 *  - Signed in: keepalive `fetch` so the Authorization header is still attached.
 *  - Logged out: `navigator.sendBeacon` (most reliable on unload, no header needed).
 */
export function sendVideoViewBeacon(
    videoId: number,
    watchSeconds: number,
    accessToken: string | null
): void {
    const url = `${WEB_API_URL}v1/videos/${videoId}/view`;
    const body = JSON.stringify({ watch_seconds: watchSeconds, platform: PLATFORM });
    const header = bearerHeader(accessToken);
    const hasToken = "Authorization" in header;

    if (!hasToken && typeof navigator !== "undefined" && navigator.sendBeacon) {
        try {
            navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
            return;
        } catch {
            /* fall through to keepalive fetch */
        }
    }

    try {
        void fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json", ...header },
            body,
            keepalive: true,
        });
    } catch {
        /* fire-and-forget */
    }
}
